import config from '../../../../config';
import { apiFetch, getAuthHeaders } from './formHttp';

// Respondent-side adapter for top-level Form Templates. Same method surface as
// triggerFormAdapter, but keyed by templateId against the standalone public
// runtime (no workflow component / version pinning, no magic-link yet).
const API = config.API_ADDRESS;

export function createTemplateAdapter(templateId, instanceId, { preview = false } = {}) {
  let _responseMode = 'FILL_SUBMIT';
  let _instanceId = instanceId || null;
  const BASE = `${API}/ai-service/public/form-templates/${templateId}`;

  function qs() {
    return preview ? '?preview=true' : '';
  }

  function qsForMeta() {
    const parts = [];
    if (preview) parts.push('preview=true');
    if (_instanceId) parts.push(`instanceId=${encodeURIComponent(_instanceId)}`);
    return parts.length ? '?' + parts.join('&') : '';
  }

  const adapter = {
    storagePrefix: 'w86_tmpl_',

    get instanceId() {
      return _instanceId;
    },

    get capabilities() {
      return {
        canSaveDraft: true,
        canUpload: true,
        hasServerValidation: true,
        hasProgressStatus: false,
        canRefresh: _responseMode === 'FILL_REEDIT',
        showConfirmationOnSubmit: _responseMode === 'FILL_SUBMIT',
      };
    },

    async bootstrap() {
      try {
        const meta = await apiFetch(`${BASE}/meta${qsForMeta()}`);
        _responseMode = meta?.responseMode || 'FILL_SUBMIT';
        const mode = String(meta?.accessPolicy?.mode || 'PUBLIC').toUpperCase();
        return {
          requiresAuth:
            mode === 'AUTHENTICATED_USERS' || mode === 'AUTHENTICATED_SELECTED_USERS',
          requiresEmailVerification: false,
          disabled: mode === 'DISABLED',
          meta,
        };
      } catch (err) {
        if (err?.response?.status === 404) return { notPublished: true };
        throw err;
      }
    },

    async loadForm() {
      let meta;
      try {
        meta = await apiFetch(`${BASE}/meta${qsForMeta()}`);
      } catch (err) {
        if (err?.response?.status === 404) {
          return { notPublished: true, fields: [], title: '', description: '' };
        }
        throw err;
      }
      _responseMode = meta?.responseMode || 'FILL_SUBMIT';
      const form = {
        fields: meta.fields || [],
        title: meta.title,
        description: meta.description,
        formSettings: meta.formSettings,
        responseMode: _responseMode,
        isPreview: meta.isPreview || false,
        sourceTemplateVersion: meta.sourceTemplateVersion ?? meta.publishedVersion ?? null,
      };
      if (meta.instanceId && !_instanceId) {
        _instanceId = meta.instanceId;
      }
      if (_instanceId) {
        try {
          const draft = await apiFetch(`${BASE}/draft/${_instanceId}${qs()}`);
          if (draft?.hasDraft) {
            if (draft.answers) form.answers = draft.answers;
            form.submitted = !!draft.submitted;
          }
        } catch {
          // No draft yet — respondent starts fresh.
        }
      }
      return form;
    },

    async saveDraft(answers) {
      const result = await apiFetch(`${BASE}/draft${qs()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers, instanceId: _instanceId }),
      });
      if (result?.instanceId) {
        _instanceId = result.instanceId;
      }
      return result;
    },

    async validate(answers) {
      return apiFetch(`${BASE}/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers,
          submitMode: false,
          instanceId: _instanceId || undefined,
        }),
      });
    },

    async submit(answers) {
      const result = await apiFetch(`${BASE}/submit${qs()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers, instanceId: _instanceId || undefined }),
      });
      if (result?.instanceId) {
        _instanceId = result.instanceId;
      }
      return result;
    },

    // Multipart upload against the public template runtime. The backend stores the blob under the
    // form owner's tenant and returns { id, fileId, name, mimeType, size }; we attach a downloadUrl
    // pointing at the public file endpoint so the renderer can link the file. The descriptor lands
    // in answers and is persisted onto the instance by the next saveDraft/submit.
    async upload(fieldKey, file) {
      const formData = new FormData();
      formData.append('file', file);
      if (fieldKey) formData.append('fieldKey', fieldKey);
      const authHeaders = await getAuthHeaders();
      const parts = [];
      if (preview) parts.push('preview=true');
      if (_instanceId) parts.push(`instanceId=${encodeURIComponent(_instanceId)}`);
      const query = parts.length ? '?' + parts.join('&') : '';
      const res = await fetch(`${BASE}/upload${query}`, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const err = new Error(body.error || 'Upload failed');
        err.response = { status: res.status, data: body };
        throw err;
      }
      const descriptor = await res.json();
      const fileId = descriptor.fileId || descriptor.id;
      return {
        ...descriptor,
        downloadUrl: descriptor.downloadUrl || `${API}/ai-service/file/get/${fileId}`,
      };
    },

    async deleteFile(fieldKey, fileId) {
      const authHeaders = await getAuthHeaders();
      const parts = [];
      if (preview) parts.push('preview=true');
      if (_instanceId) parts.push(`instanceId=${encodeURIComponent(_instanceId)}`);
      const query = parts.length ? '?' + parts.join('&') : '';
      const res = await fetch(
        `${BASE}/upload/${encodeURIComponent(fileId)}${query}`,
        { method: 'DELETE', headers: authHeaders }
      );
      if (!res.ok && res.status !== 204) {
        const body = await res.json().catch(() => ({}));
        const err = new Error(body.error || 'Delete failed');
        err.response = { status: res.status, data: body };
        throw err;
      }
    },

    deriveReadOnly(form) {
      if (!form) return false;
      const rm = form?.responseMode || _responseMode;
      return rm === 'VIEW_ONLY';
    },

    deriveCanSubmit(form) {
      if (!form) return true;
      const rm = form?.responseMode || _responseMode;
      return rm !== 'VIEW_ONLY';
    },

    deriveCanEdit(form) {
      if (!form) return true;
      const rm = form?.responseMode || _responseMode;
      return rm !== 'VIEW_ONLY';
    },
  };

  return adapter;
}
